/**
 * usePagesState - Custom hook for CMS Pages module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

export const usePagesState = ({ setError, setSuccess, setLoading }) => {
  // Pages state
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);

  // Fetch all pages
  const fetchPages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/cms/pages`);
      setPages(response.data);
    } catch (err) {
      setError('Failed to fetch pages: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading]);

  // Fetch page details by ID
  const fetchPageDetails = useCallback(async (pageId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/cms/pages/by-id/${pageId}`);
      setSelectedPage(response.data);
    } catch (err) {
      setError('Failed to fetch page details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading]);

  // Update a page field
  const updatePageField = useCallback(async (pageId, field, value) => {
    try {
      const updateData = { [field]: value };
      await axios.put(`${API_URL}/api/cms/pages/${pageId}`, updateData);
      setSuccess(`Page ${field} updated successfully`);
      setTimeout(() => setSuccess(null), 3000);
      fetchPages();
      setSelectedPage(prev => {
        if (prev && prev.id === pageId) {
          return { ...prev, [field]: value };
        }
        return prev;
      });
    } catch (err) {
      setError('Failed to update page: ' + err.message);
    }
  }, [fetchPages, setError, setSuccess]);

  // Update a section field
  const updateSectionField = useCallback(async (sectionId, field, value) => {
    try {
      const updateData = { [field]: value };
      await axios.put(`${API_URL}/api/cms/sections/${sectionId}`, updateData);
      setSuccess(`Section ${field} updated successfully`);
      setTimeout(() => setSuccess(null), 3000);
      // Refresh page details
      setSelectedPage(prev => {
        if (prev) {
          fetchPageDetails(prev.id);
        }
        return prev;
      });
    } catch (err) {
      setError('Failed to update section: ' + err.message);
    }
  }, [fetchPageDetails, setError, setSuccess]);

  // Update a block field
  const updateBlockField = useCallback(async (blockId, field, value) => {
    try {
      const updateData = { [field]: value };
      await axios.put(`${API_URL}/api/cms/blocks/${blockId}`, updateData);
      setSuccess(`Block ${field} updated successfully`);
      setTimeout(() => setSuccess(null), 3000);
      // Refresh page details
      setSelectedPage(prev => {
        if (prev) {
          fetchPageDetails(prev.id);
        }
        return prev;
      });
    } catch (err) {
      setError('Failed to update block: ' + err.message);
    }
  }, [fetchPageDetails, setError, setSuccess]);

  // Update block content
  const updateBlockContent = useCallback(async (blockId, contentKey, contentValue) => {
    try {
      // Fetch current block to get existing content
      const blockResponse = await axios.get(`${API_URL}/api/cms/blocks/${blockId}`);
      const currentBlock = blockResponse.data;
      
      // Update the content object
      const updatedContent = { ...currentBlock.content, [contentKey]: contentValue };
      
      await axios.put(`${API_URL}/api/cms/blocks/${blockId}`, { content: updatedContent });
      setSuccess('Block content updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      // Refresh page details
      setSelectedPage(prev => {
        if (prev) {
          fetchPageDetails(prev.id);
        }
        return prev;
      });
    } catch (err) {
      setError('Failed to update block content: ' + err.message);
    }
  }, [fetchPageDetails, setError, setSuccess]);

  return {
    // State
    pages,
    selectedPage,

    // Setters
    setPages,
    setSelectedPage,

    // Actions
    fetchPages,
    fetchPageDetails,
    updatePageField,
    updateSectionField,
    updateBlockField,
    updateBlockContent,
  };
};

export default usePagesState;
